/**
 * Confetti with LS colors and slower, floatier presets.
 *
 * To be used with CelebrationWrapper.
 */
import * as React from "react";

import {Confetti, dataURIToImage} from "@khan/component-confetti";

import type {CelebrationProps} from "./celebration-types.ts";
import {
    blueConfettiPNG,
    bluePlanetConfettiPNG,
    blueStarCutOutConfettiPNG,
    greenConfettiPNG,
    greenPlanetConfettiPNG,
    orangePlanetConfettiPNG,
    orangeScribbleStarCutOutConfettiPNG,
    orangeStarConfettiPNG,
    redConfettiPNG,
    springConfettiPNG,
    whiteAsteriskConfettiPNG,
    whiteOrangeStarCutOutConfettiPNG,
    yellowConfettiPNG,
    yellowStarConfettiPNG,
} from "./confetti-kinds.js";

export type ConfettiShape = {
    front: HTMLImageElement;
    back: HTMLImageElement;
};
// Shapes must have a front and back even though we aren't currently using the
// twist prop
export const defaultShapes: Array<ConfettiShape> = [
    {
        front: dataURIToImage(blueConfettiPNG),
        back: dataURIToImage(blueConfettiPNG),
    },
    {
        front: dataURIToImage(greenConfettiPNG),
        back: dataURIToImage(greenConfettiPNG),
    },
    {
        front: dataURIToImage(redConfettiPNG),
        back: dataURIToImage(redConfettiPNG),
    },
    {
        front: dataURIToImage(springConfettiPNG),
        back: dataURIToImage(springConfettiPNG),
    },
    {
        front: dataURIToImage(whiteAsteriskConfettiPNG),
        back: dataURIToImage(whiteAsteriskConfettiPNG),
    },
    {
        front: dataURIToImage(yellowConfettiPNG),
        back: dataURIToImage(yellowConfettiPNG),
    },
];

const warmColorShapes: Array<ConfettiShape> = [
    {
        front: dataURIToImage(springConfettiPNG),
        back: dataURIToImage(springConfettiPNG),
    },
    {
        front: dataURIToImage(whiteAsteriskConfettiPNG),
        back: dataURIToImage(whiteAsteriskConfettiPNG),
    },
    {
        front: dataURIToImage(redConfettiPNG),
        back: dataURIToImage(redConfettiPNG),
    },
    {
        front: dataURIToImage(yellowConfettiPNG),
        back: dataURIToImage(yellowConfettiPNG),
    },
    {
        front: dataURIToImage(yellowConfettiPNG),
        back: dataURIToImage(yellowConfettiPNG),
    },
    {
        front: dataURIToImage(yellowConfettiPNG),
        back: dataURIToImage(yellowConfettiPNG),
    },
];

const coolColorShapes: Array<ConfettiShape> = [
    {
        front: dataURIToImage(blueConfettiPNG),
        back: dataURIToImage(blueConfettiPNG),
    },
    {
        front: dataURIToImage(greenConfettiPNG),
        back: dataURIToImage(greenConfettiPNG),
    },
    {
        front: dataURIToImage(springConfettiPNG),
        back: dataURIToImage(springConfettiPNG),
    },
];

const yellowShapes: Array<ConfettiShape> = [
    {
        front: dataURIToImage(yellowStarConfettiPNG),
        back: dataURIToImage(yellowStarConfettiPNG),
    },
    {
        front: dataURIToImage(whiteAsteriskConfettiPNG),
        back: dataURIToImage(whiteAsteriskConfettiPNG),
    },
    {
        front: dataURIToImage(yellowStarConfettiPNG),
        back: dataURIToImage(yellowStarConfettiPNG),
    },
    {
        front: dataURIToImage(yellowConfettiPNG),
        back: dataURIToImage(yellowConfettiPNG),
    },
    {
        front: dataURIToImage(yellowConfettiPNG),
        back: dataURIToImage(yellowConfettiPNG),
    },
];

const greenShapes: Array<ConfettiShape> = [
    {
        front: dataURIToImage(greenConfettiPNG),
        back: dataURIToImage(greenConfettiPNG),
    },
    {
        front: dataURIToImage(whiteAsteriskConfettiPNG),
        back: dataURIToImage(whiteAsteriskConfettiPNG),
    },
    {
        front: dataURIToImage(springConfettiPNG),
        back: dataURIToImage(springConfettiPNG),
    },
    {
        front: dataURIToImage(greenPlanetConfettiPNG),
        back: dataURIToImage(greenPlanetConfettiPNG),
    },
];

const orangeShapes: Array<ConfettiShape> = [
    {
        front: dataURIToImage(springConfettiPNG),
        back: dataURIToImage(springConfettiPNG),
    },
    {
        front: dataURIToImage(orangeStarConfettiPNG),
        back: dataURIToImage(orangeStarConfettiPNG),
    },
    {
        front: dataURIToImage(redConfettiPNG),
        back: dataURIToImage(redConfettiPNG),
    },
    {
        front: dataURIToImage(orangeScribbleStarCutOutConfettiPNG),
        back: dataURIToImage(orangeScribbleStarCutOutConfettiPNG),
    },
];

const blueShapes: Array<ConfettiShape> = [
    {
        front: dataURIToImage(blueStarCutOutConfettiPNG),
        back: dataURIToImage(blueStarCutOutConfettiPNG),
    },
    {
        front: dataURIToImage(springConfettiPNG),
        back: dataURIToImage(springConfettiPNG),
    },
    {
        front: dataURIToImage(blueConfettiPNG),
        back: dataURIToImage(blueConfettiPNG),
    },
    {
        front: dataURIToImage(whiteAsteriskConfettiPNG),
        back: dataURIToImage(whiteAsteriskConfettiPNG),
    },
];

const planetShapes: Array<ConfettiShape> = [
    {
        front: dataURIToImage(redConfettiPNG),
        back: dataURIToImage(redConfettiPNG),
    },
    {
        front: dataURIToImage(orangePlanetConfettiPNG),
        back: dataURIToImage(orangePlanetConfettiPNG),
    },
    {
        front: dataURIToImage(bluePlanetConfettiPNG),
        back: dataURIToImage(bluePlanetConfettiPNG),
    },
    {
        front: dataURIToImage(springConfettiPNG),
        back: dataURIToImage(springConfettiPNG),
    },
    {
        front: dataURIToImage(whiteAsteriskConfettiPNG),
        back: dataURIToImage(whiteAsteriskConfettiPNG),
    },
    {
        front: dataURIToImage(whiteAsteriskConfettiPNG),
        back: dataURIToImage(whiteAsteriskConfettiPNG),
    },
    {
        front: dataURIToImage(whiteAsteriskConfettiPNG),
        back: dataURIToImage(whiteAsteriskConfettiPNG),
    },
];

const starShapes: Array<ConfettiShape> = [
    {
        front: dataURIToImage(whiteAsteriskConfettiPNG),
        back: dataURIToImage(whiteAsteriskConfettiPNG),
    },
    {
        front: dataURIToImage(blueStarCutOutConfettiPNG),
        back: dataURIToImage(blueStarCutOutConfettiPNG),
    },
    {
        front: dataURIToImage(whiteOrangeStarCutOutConfettiPNG),
        back: dataURIToImage(whiteOrangeStarCutOutConfettiPNG),
    },
    {
        front: dataURIToImage(orangeScribbleStarCutOutConfettiPNG),
        back: dataURIToImage(orangeScribbleStarCutOutConfettiPNG),
    },
];

// the list has repeats because level 5 and 10
// are bounce celebrations, not confetti.
// this does mean that on random mode, we see a little more
// starShapes and greenShapes
const shapesList = [
    defaultShapes,
    coolColorShapes,
    warmColorShapes,
    yellowShapes,
    starShapes,
    greenShapes,
    blueShapes,
    orangeShapes,
    planetShapes,
    greenShapes,
    starShapes,
];

export default class LSConfetti extends React.Component<CelebrationProps> {
    componentDidMount() {
        if (this.confetti && this.confetti.current) {
            this.confetti.current.startConfetti(this.props.width / 2, -30);
        }

        // TODO(jeff, MP-2132): Use Wonder Blocks Timing API
        // eslint-disable-next-line no-restricted-syntax
        setTimeout(this.props.onAnimationComplete, 7000);
    }

    confetti = React.createRef<React.ElementRef<typeof Confetti>>();

    render(): React.ReactNode {
        // default levelFactor to 1
        const levelFactor = this.props.levelFactor || 1;
        const latestLevel = this.props.latestLevel;

        let shapesTheme = defaultShapes;
        if (latestLevel && latestLevel <= 11) {
            shapesTheme = shapesList[latestLevel - 1];
        } else if (latestLevel && latestLevel > 11) {
            shapesTheme =
                shapesList[Math.floor(Math.random() * shapesList.length)];
        }

        return (
            <Confetti
                ref={this.confetti}
                width={this.props.width}
                height={this.props.height}
                gravity={2000 * levelFactor}
                numParticles={this.props.width * levelFactor * 0.1}
                spreadHorizontally={true}
                emitDuration={5000}
                shapes={shapesTheme}
                spin={10}
                twist={0}
                minSpeed={100}
                maxSpeed={200}
                minScale={1}
                maxScale={1}
            />
        );
    }
}
